import { SemantriaConfigurationDto } from '../../../../providers/semantria-api/configuration.service';
import { ImportStrategy } from './import-strategy';

export class MergeImportStrategy implements ImportStrategy {
  private readonly targetTypeToInclude = {
    QUERY_TOPIC: 'query_topics',
    USER_ENTITY: 'user_entities',
    CONCEPT_TOPIC: 'concept_topics',
  };

  private deDup = (source: any[], keyFn) => {
    return [...new Map(source.map((el) => [keyFn(el), el])).values()];
  };

  private findDups = (source: any[], target: any[], keyFn) => {
    const targetKeys = new Set(target.map((el) => keyFn(el)));

    return source.filter((item) => targetKeys.has(keyFn(item)));
  };

  private updateDups = (
    source: any[],
    target: any[],
    keyFn
  ): { idMap: Map<string, string>; items: any[] } => {
    const targetMap = new Map(target.map((el) => [keyFn(el), el]));

    const dups = {
      idMap: new Map<string, string>(),
      items: source.filter((item) => targetMap.has(keyFn(item))),
    };

    dups.items.forEach((item) => {
      const targetId = targetMap.get(keyFn(item)).id;
      dups.idMap.set(item.id, targetId);
      item.id = targetId;
    });

    return dups;
  };

  doImport = (source, target, configIncludeData) => {
    const dto = JSON.parse(JSON.stringify(target));
    const itemsToCreate: any = {};
    const itemsToDelete: any = {};
    const itemsToUpdate: any = {};

    const keyFnFactory = (prop) => {
      switch (prop) {
        case 'user_entities':
          return (item) => item.type + (item.normalized || item.name);
        case 'target':
          return (item) => item.target_id;
        default:
          return (item) => item.name;
      }
    };

    dto.notes = this.assembleNotes(source, target);
    dto.tags = this.assembleTags(source, target);

    if (!configIncludeData.includes.notes) {
      dto.notes = undefined;
    }

    if (!configIncludeData.includes.tags) {
      dto.tags = undefined;
    }

    const idMap: any = {};

    Object.keys(configIncludeData.includes)
      .filter((k) => k !== 'notes' && k !== 'tags')
      .filter((k) => configIncludeData.includes[k])
      .forEach((k) => {
        const safeSource = source[k] || [];
        const duplicates = this.updateDups(safeSource, target[k] || [], keyFnFactory(k));
        idMap[k] = duplicates.idMap;
        itemsToCreate[k] = safeSource.filter((s) => !duplicates.items.includes(s));
        itemsToUpdate[k] = duplicates.items;
        dto[k] = dto[k] || [];
        dto[k] = [
          ...dto[k].filter((i) => duplicates.items.every((di) => di.id !== i.id)),
          ...itemsToUpdate[k],
          ...itemsToCreate[k],
        ];
      });

    if (configIncludeData.includes.taxonomies) {
      const taxonomyKeyFn = keyFnFactory('taxonomies');
      const mergeTaxonomyNodes = (sourceNode, targetNode) => {
        if (sourceNode.elements || targetNode.elements) {
          sourceNode.elements = sourceNode.elements || [];
          sourceNode.elements.unshift(...(targetNode.elements || []));
        }

        if (sourceNode.children) {
          if (targetNode.children) {
            const duplicateChildren = this.findDups(
              sourceNode.children,
              targetNode.children,
              taxonomyKeyFn
            );

            for (const child of duplicateChildren) {
              const childNodeKey = taxonomyKeyFn(child);

              mergeTaxonomyNodes(
                sourceNode.children.find((t) => taxonomyKeyFn(t) === childNodeKey),
                targetNode.children.find((t) => taxonomyKeyFn(t) === childNodeKey)
              );
            }

            sourceNode.children = [
              ...targetNode.children.filter((tn) =>
                duplicateChildren.every((c) => taxonomyKeyFn(c) !== taxonomyKeyFn(tn))
              ),
              ...sourceNode.children,
            ];
          }
        } else if (targetNode.children) {
          sourceNode.children = [...targetNode.children];
        }
      };

      for (const updatedNode of itemsToUpdate.taxonomies) {
        const updatedNodeKey = taxonomyKeyFn(updatedNode);

        mergeTaxonomyNodes(
          source.taxonomies.find((t) => taxonomyKeyFn(t) === updatedNodeKey),
          target.taxonomies.find((t) => taxonomyKeyFn(t) === updatedNodeKey)
        );
      }

      const targetKeyFn = keyFnFactory('target');
      const replaceTargetItemIds = (taxonomyNodes) => {
        if (taxonomyNodes) {
          taxonomyNodes.forEach((node) => {
            if (node.elements) {
              node.elements.forEach((el) => {
                el.target_id =
                  idMap[this.targetTypeToInclude[el.target_type]]?.get(el.target_id) ||
                  el.target_id;
              });
              node.elements = this.deDup(node.elements, targetKeyFn);
            }

            replaceTargetItemIds(node.children);
          });
        }
      };

      replaceTargetItemIds(itemsToCreate.taxonomies);
      replaceTargetItemIds(itemsToUpdate.taxonomies);
    }

    return {
      configDto: dto,
      itemsToCreate,
      itemsToDelete,
      itemsToUpdate,
    };
  };

  countItems = (source, target, itemProps) => {
    const count = new Map<string, number>();

    for (let index = 0; index < itemProps.length; index++) {
      const item = itemProps[index];
      const safeSource = source[item.prop] || [];
      const safeTarget = target[item.prop] || [];

      const duplicates = this.findDups(safeSource, safeTarget, (i) => i.normalized || i.name);

      count.set(item.prop, safeSource.length + safeTarget.length - duplicates.length);
    }

    return count;
  };

  assembleNotes = (source: SemantriaConfigurationDto, target: SemantriaConfigurationDto) => {
    return `${target.notes || ''} ${source.notes || ''}`;
  };

  assembleTags = (source: SemantriaConfigurationDto, target: SemantriaConfigurationDto) => {
    const safeSource = source.tags || [];
    const safeTarget = target.tags || [];
    const duplicates = this.findDups(safeSource, safeTarget, (key) => key);
    return safeTarget.concat(safeSource.filter((tag) => !duplicates.includes(tag)));
  };
}
